import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, effect, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';
import { AppStateService } from 'app/app-state.service';
import { AppState } from 'app/app.state';
import { EnvironmentService } from 'app/core/services/environment.service';
import { SessionIdleService } from 'app/session-idle/session-idle.service';
import { GetIPService } from 'app/shared/services/get-ip/get-ip.service';
import { NotificationService } from 'app/shared/services/notification/notification.service';
import { UtilityService } from 'app/shared/services/utility/utility.service';
import { SupportScriptService } from 'app/support-scripts/support-script.service';
import dayjs from 'dayjs';
import { environment } from 'environments/environment';
import { jwtDecode } from 'jwt-decode';
import { EMPTY, firstValueFrom, forkJoin, iif, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import SafeUrlAssembler from 'safe-url-assembler';
import { ApiEndpointService } from '../api-endpoint.service';
import { SharedService } from '../shared.service';
import { StorageService } from '../storage/storage.service';
import { ILoginResponse } from './models/login-response.interface';
import { IRecaptchaSiteVerifyRequest } from './models/recaptcha-site-verify-request.interface';
import { IRecaptchaSiteVerifyResponse } from './models/recaptcha-site-verify-response.interface';

interface IAuthResponse {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    token_type: string;
}

@Injectable()
export class AuthenticationService {
    private loggedIn = signal(false);
    public isLoggedInSignal = computed(() => this.loggedIn());

    private _isLoggedIn = false;
    private _authToken: string;
    private nonInterceptHttpClient: HttpClient;
    private httpOptions = {
        headers: new HttpHeaders({
            'Accept-Language': sessionStorage.getItem('lang') ? sessionStorage.getItem('lang') : 'en',
            'Content-Type': 'application/x-www-form-urlencoded',
        }),
    };
    private refreshTokenTimer: NodeJS.Timeout;

    public constructor(private httpClient: HttpClient,
                       private httpBackend: HttpBackend,
                       private router: Router,
                       private apiEndpointService: ApiEndpointService,
                       private storageService: StorageService,
                       private storage: StorageMap,
                       private sharedService: SharedService,
                       private readonly utilityService: UtilityService,
                       private readonly supportScriptService: SupportScriptService,
                       private readonly appState: AppState,
                       private readonly appStateService: AppStateService,
                       private readonly getIpService: GetIPService,
                       private readonly notificationService: NotificationService,
                       private readonly sessionIdleService: SessionIdleService,
                       private readonly environmentService: EnvironmentService,
    ) {
        this.nonInterceptHttpClient = new HttpClient(this.httpBackend);

        this.storage.get('auth_token').subscribe(
            (token: string) => {
                this._authToken = token;
                this._isLoggedIn = !!token;
            },
        );

        effect(() => {
            const last = this.sessionIdleService.lastActivity();
            const now = Date.now();

            const timeSinceLast = now - last;

            // refresh if nearing expiration
            const refreshThresholdMs = (environment.refreshTokenConfig?.offsetInMinutes || 5) * 60 * 1000;
            if (timeSinceLast > refreshThresholdMs) {
                console.log('Refreshing token...');
                this.refreshToken();
            }
        });
    }

    private _redirectUrl = '';

    /**
     * Gets the redirect URL.
     * This value is set by the loggedin-guard to keep the URL a user is trying to access.
     * Later, it can be retrieved and the system can then redirect the user to the URL.
     *
     * @returns {string} The redirect URL.
     */
    public get redirectUrl() {
        return this._redirectUrl;
    }

    /**
     * Sets the redirect URL.
     * This value is set by the logged-in-guard to keep the URL a user is trying to access.
     * Later, it can be retrieved and the system can then redirect the user to the URL.
     *
     * @param url The redirect URL.
     */
    public set redirectUrl(url: string) {
        this._redirectUrl = url;
    }

    public get token() {
        return this._authToken;
    }

    /**
     * Gets the value indicating whether a user is logged-in.
     */
    public isLoggedIn(): Observable<boolean> {
        // TEMP: Remove this when done with storage refactoring.
        this.storageService.storage = sessionStorage;

        return this.storage.get('auth_token')
                   .pipe(
                       map(token => !!token),
                       tap((hasToken: boolean) => {
                           this.loggedIn.set(hasToken);
                       }),
                   );
        // if (!this.storageService.storage) {
        //     this.setStorage();
        //
        //     if (!this.storageService.storage) {
        //         return false;
        //     }
        // }
        //
        // const token = this.storageService.getAsText('auth_token');
        //
        // return !!token;
    }

    public login(username: string, password: string, isRememberMe = false): Observable<ILoginResponse> {
        const envAlias = this.environmentService.getEnvironmentAlias();
        const url = `https://${ envAlias ? `auth-${ envAlias }` : 'auth' }.parcelpro.com/api/login`;
        const endpoint = SafeUrlAssembler().template(url).toString();
        // const headers = this.getLoginRequestHeaders(true);
        const data = `username=${ encodeURIComponent(username) }&password=${ encodeURIComponent(password) }&grant_type=password`;

        return this.httpClient.post<IAuthResponse>(endpoint, data, this.httpOptions)
                   .pipe(
                       catchError((err) => {
                           this.clearAppState();
                           return throwError(() => err);
                       }),
                       switchMap(res => this.handleAuthenticationSuccessResponse(res, isRememberMe)),
                   );
    }

    public secureLogin(username: string, password: string, isRememberMe = false): Observable<ILoginResponse> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.endpointUrls.login).toString();

        const body = new URLSearchParams();
        body.append('username', username);
        body.append('password', password);
        body.append('grant_type', 'password');

        return this.nonInterceptHttpClient.post<IAuthResponse>(endpoint, body.toString(), this.httpOptions)
                   .pipe(
                       catchError((err) => {
                           this.clearAppState();
                           return throwError(() => err);
                       }),
                       switchMap(res => this.handleAuthenticationSuccessResponse(res, isRememberMe)),
                   );
    }

    public refreshToken(): void {
        this.storage.get('refresh_token')
            .pipe(
                switchMap((refreshToken: string) => {
                    let endpoint = SafeUrlAssembler()
                        .template(this.apiEndpointService.endpointUrls.refreshToken)
                        .toString();

                    if (!environment.feature.coreAuth) {
                        const envAlias = this.environmentService.getEnvironmentAlias();
                        const url = `https://${ envAlias ? `auth-${ envAlias }` : 'auth' }.parcelpro.com/api/refresh/validate`;
                        endpoint = SafeUrlAssembler().template(url).toString();
                    }

                    const data = { refresh_token: refreshToken };

                    // this.appState.refreshTokenCountSignal.update(count => count + 1);
                    // this.notificationService.notify(
                    //     `Refreshing token...${ this.appState.refreshTokenCountSignal() }`,
                    //     'Refreshing Token',
                    //     NotificationType.INFO,
                    // );
                    // console.log(`Refreshing token...${ this.appState.refreshTokenCountSignal() }`);

                    return this.httpClient.post<IAuthResponse>(endpoint, data, this.httpOptions)
                               .pipe(
                                   catchError(async () => {
                                       // Remove this session expiration notification message as requested.
                                       // this.notificationService.notify(
                                       //     'You will be securely logged out. Please log in again to continue.',
                                       //     'Session Timeout',
                                       //     NotificationType.WARNING,
                                       // );

                                       await this.logout();
                                       return EMPTY;
                                   }),
                                   switchMap((res) => {
                                       return this.handleAuthenticationSuccessResponse(res as IAuthResponse);
                                   }),
                               );
                }),
            ).subscribe(() => {
        });
    }

    /**
     * Set up a timer to refresh auth token.
     * @param offset - time (in minutes) to trigger a token refresh before expiration
     */
    public setupRefreshTokenTimer(offset: number): void {
        if (!environment.feature.refreshToken || !offset) {
            return;
        }

        forkJoin({
            authToken: this.storage.get('auth_token') as Observable<string>,
            refreshToken: this.storage.get('refresh_token') as Observable<string>,
            // exp: this.storage.get('expires_in') as Observable<number>,
        }).subscribe(
            async ({ authToken, refreshToken }) => {
                if (!authToken || !refreshToken) {
                    return;
                }

                const jwtPayload = jwtDecode(authToken);
                const expiration = dayjs.unix(jwtPayload.exp);
                // const expiration = dayjs().add(exp, 'seconds');

                if (expiration.isBefore()) {
                    await this.logout();
                    return;
                }

                const triggerAt = expiration.add(0 - offset, 'minutes');
                const diff = triggerAt.diff(dayjs());

                this.refreshTokenTimer = setTimeout(() => this.refreshToken(), diff);
            },
        );
    }

    /**
     * Logs the user out of the system.
     * This will prevent a user from accessing private sections of the site.
     * All stored token will be cleared.
     */
    public async logout(isRedirect = true): Promise<void> {
        this._isLoggedIn = false;
        this.loggedIn.set(false);
        this.supportScriptService.loadDefaultScript();
        clearTimeout(this.refreshTokenTimer);
        await firstValueFrom(this.storage.clear());
        this.storageService.clear();
        this.storageService.storage = null;
        localStorage.clear();
        this.redirectUrl = null;

        this.clearAppState();
        this.sessionIdleService.stop();

        const siteLanguage = this.utilityService.getSiteLanguage();
        sessionStorage.setItem('lang', siteLanguage);
        sessionStorage.setItem('site-locale', siteLanguage);

        forkJoin([
            this.storage.set('lang', siteLanguage),
            this.storage.set('site-locale', siteLanguage),
        ]).subscribe(() => {
        });

        if (isRedirect) {
            // await this.router.navigate(['/login']);

            // This will make sure that all support scripts are reset.
            // window.location.reload();
            (window as any).location = '/login';
        }
    }

    /**
     * Sends a request to the API notifying that the user of the specified email address requests a password reset.
     *
     * @param username
     * @returns {Observable<R>}
     */
    public resetPassword(username: string) {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('resetPassword'))
            .param('id', username)
            .toString();
        // const headers = this.getLoginRequestHeaders();
        const data = { username };

        return this.httpClient.post<any>(endpoint, data);
    }

    /**
     * request password reset.
     *
     * @param email
     * @param username
     * @returns {Observable<R>}
     */
    public passwordReset(email: string, username: string) {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('passwordReset'))
                                           .toString();
        // const headers = this.getLoginRequestHeaders();
        const data = {
            UserName: username,
            Email: email,
        };

        return this.nonInterceptHttpClient.post<any>(endpoint, data);
    }

    public loginWithToken(token: string, shouldClearStorage = true): Observable<ILoginResponse> {
        this._isLoggedIn = false;
        this.storageService.storage = sessionStorage;

        if (shouldClearStorage) {
            this.storageService.clear();
        }

        this.clearAppState();

        const siteLanguage = this.utilityService.getSiteLanguage();
        const clearStorageAndSetLanguage$ = this.storage.clear().pipe(
            switchMap(() => this.storage.set('lang', siteLanguage)),
            switchMap(() => this.storage.set('site-locale', siteLanguage)),
        );

        let endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.tokenLogin)
            .param('loginToken', token)
            .toString();

        if (!environment.feature.coreAuth) {
            const envAlias = this.environmentService.getEnvironmentAlias();
            const url = `https://${ envAlias ? `auth-${ envAlias }` : 'auth' }.parcelpro.com/api/intratoship-jwt/:loginToken`;
            endpoint = SafeUrlAssembler().template(url).param('loginToken', token).toString();
        }

        const login$ = this.httpClient.post<IAuthResponse>(endpoint, {})
                           .pipe(
                               switchMap(res => this.handleAuthenticationSuccessResponse(res, false)),
                           );

        return iif(() => shouldClearStorage, clearStorageAndSetLanguage$, EMPTY)
            .pipe(
                switchMap(() => login$),
            );
    }

    public verifyReCaptcha(token: string): Observable<IRecaptchaSiteVerifyResponse> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('reCaptchaSiteVerify')).toString();
        const data: IRecaptchaSiteVerifyRequest = {
            reCaptchaToken: token,
        };

        return this.httpClient.post<IRecaptchaSiteVerifyResponse>(endpoint, data);
    }

    private setStorage() {
        let token = localStorage.getItem('auth_token');
        if (token && token !== '') {
            this.storageService.storage = localStorage;
            return;
        }

        token = sessionStorage.getItem('auth_token');
        if (token && token !== '') {
            this.storageService.storage = sessionStorage;
        }
    }

    /**
     * Gets the login request headers.
     *
     * @param isFormLogin True if the login is a form login; otherwise, false.
     * @returns {Headers} The headers for logging in.
     */
    private getLoginRequestHeaders(isFormLogin: boolean = false): Headers {
        const headers = new Headers();
        let contentType = 'application/json';

        if (isFormLogin) {
            contentType = 'application/x-www-form-urlencoded';
        }

        headers.append('Content-Type', contentType);

        return headers;
    }

    private handleAuthenticationSuccessResponse(json: IAuthResponse, isRememberMe = false): Observable<ILoginResponse> {
        if (environment.feature?.sessionIdle?.enabled) {
            this.sessionIdleService.reset();
        }

        // const json = res.json();
        this._isLoggedIn = true;
        this.loggedIn.set(true);
        return this.storeToken(json, isRememberMe);
    }

    private storeToken(tokenJson: IAuthResponse, isRememberMe = false): Observable<ILoginResponse> {
        if (!tokenJson) {
            return of(null);
        }

        // this.storageService.storage = isRememberMe ? localStorage : sessionStorage;
        // this.storageService.set('auth_token', tokenJson.access_token);
        // this.storageService.set('auth_token_type', tokenJson.token_type);
        // this._isLoggedIn = true;

        this._authToken = tokenJson.access_token;
        return forkJoin([
            this.storage.set('auth_token', tokenJson.access_token),
            this.storage.set('auth_token_type', tokenJson.token_type),
            this.storage.set('refresh_token', tokenJson.refresh_token),
            this.storage.set('expires_in', tokenJson.expires_in),
        ]).pipe(
            tap(() => this.setupRefreshTokenTimer(environment.refreshTokenConfig?.offsetInMinutes)),
            switchMap(() => this.appStateService.refreshAppState()
                                .pipe(
                                    map(appState => ({
                                            user: appState.user,
                                            customer: appState.customer,
                                            auth: tokenJson,
                                        }),
                                    ),
                                )),
        );
    }

    private clearAppState(): void {
        this.appState.user$.set(null);
        this.appState.customer$.set(null);
        this.appState.userRoutes$.set(null);
    }
}
